import { useEffect, useState, useCallback } from "react";
import { DownloadSimple, DeviceMobile, X, ShareNetwork, Plus } from "@phosphor-icons/react";

// Standalone check — the user has already installed & is running the PWA.
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

const DISMISS_KEY = "alaxe.pwa.installDismissed";
const IOS_HELP_KEY = "alaxe.pwa.iosHelpDismissed";

// Shared hook: exposes the deferred install event + install trigger.
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBip = (e) => {
      // Prevent the default mini-infobar and stash the event.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    return choice?.outcome || null;
  }, [deferredPrompt]);

  return {
    canPrompt: Boolean(deferredPrompt),
    installed,
    ios: isIos(),
    promptInstall,
  };
}

// ---------------- Dashboard banner (dismissible) ----------------
export function InstallBanner() {
  const { canPrompt, installed, ios, promptInstall } = usePwaInstall();
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(DISMISS_KEY) === "1";
  });
  const [showIosHelp, setShowIosHelp] = useState(false);

  if (installed || dismissed) return null;
  // Only show if we can actually prompt (Android/Desktop) OR we're on iOS
  // where we surface an "Add to Home Screen" hint.
  if (!canPrompt && !ios) return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  const onInstall = async () => {
    if (ios) {
      setShowIosHelp(true);
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted") dismiss();
  };

  return (
    <div
      data-testid="pwa-install-banner"
      className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-center gap-4"
    >
      <div className="w-11 h-11 rounded-xl al-gradient text-white grid place-items-center shrink-0">
        <DeviceMobile size={22} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
          Install AL-AXE
        </div>
        <div className="text-sm md:text-base font-medium text-slate-900 mt-0.5">
          Add AL-AXE to your home screen for one-tap capture, offline access, and a full-screen experience.
        </div>
        {showIosHelp && (
          <div
            data-testid="pwa-ios-help"
            className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3 leading-relaxed"
          >
            On iPhone / iPad: tap the <ShareNetwork size={14} className="inline align-[-2px]" /> <b>Share</b> button in Safari, then choose <Plus size={14} className="inline align-[-2px]" /> <b>Add to Home Screen</b>.
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={onInstall}
          data-testid="pwa-install-btn"
          className="inline-flex items-center gap-2 al-gradient text-white px-4 py-2 rounded-full text-sm font-medium active:scale-95 transition-transform"
        >
          <DownloadSimple size={16} weight="bold" />
          {ios ? "How to install" : "Install"}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-dismiss-btn"
          aria-label="Dismiss install banner"
          className="w-9 h-9 rounded-full hover:bg-slate-100 grid place-items-center text-slate-500"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// ---------------- More-sheet row (subtle nav-style entry) ----------------
export function InstallMoreRow({ onDone }) {
  const { canPrompt, installed, ios, promptInstall } = usePwaInstall();
  const [showIosHelp, setShowIosHelp] = useState(false);

  if (installed) return null;
  if (!canPrompt && !ios) return null;

  const onClick = async () => {
    if (ios) {
      setShowIosHelp((v) => !v);
      // remember they've seen the hint so we don't nag on the banner
      try { localStorage.setItem(IOS_HELP_KEY, "1"); } catch {}
      return;
    }
    const outcome = await promptInstall();
    if (outcome === "accepted" && onDone) onDone();
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="more-pwa-install"
        className="w-full flex items-center gap-3 px-3 py-3.5 rounded-xl text-sm font-medium min-h-[52px] text-slate-800 hover:bg-slate-100 active:bg-slate-100"
      >
        <DownloadSimple size={20} weight="duotone" />
        {ios ? "Add AL-AXE to Home Screen" : "Install AL-AXE app"}
      </button>
      {showIosHelp && (
        <div
          data-testid="more-pwa-ios-help"
          className="mx-3 mt-1 mb-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3 leading-relaxed"
        >
          In Safari, tap the <ShareNetwork size={14} className="inline align-[-2px]" /> <b>Share</b> button, then <Plus size={14} className="inline align-[-2px]" /> <b>Add to Home Screen</b>.
        </div>
      )}
    </>
  );
}
